/* eslint-disable react-hooks/exhaustive-deps */
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import SharesChart from "components/charts/modern/SharesChart";
import TradesChart from "components/charts/modern/TradesChart";
import TrailingReturnsChart from "components/charts/modern/TrailingReturnsChart";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import CompanyIssuerGovernance from "components/company/v2/CompanyIssuerGovernance";
import CompanyIssuerHeaderCard from "components/company/v2/CompanyIssuerHeaderCard";
import ImpostoDeRenda from "components/company/v2/ImpostoDeRenda";
import YouTubeVideos from "components/company/v2/YouTubeVideos";
import FoundedYearCard from "components/company/v2/cards/FoundedYearCard";
import IndexParticipationCard from "components/company/v2/cards/IndexParticipationCard";
import MarketCapCard from "components/company/v2/cards/MarketCapCard";
import Financials from "components/financials/modern/Financials";
import LocalFinancialsState from "components/financials/modern/LocalFinancialsState";
import Rating from "components/rating/Rating";
import DistributionOfAssets from "components/realestatefunds/DistributionOfAssets";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import TwoPassInViewRender from "components/shared/TwoPassInViewRender";
import { ScrollJumpButton, SpanPixelBeacon, StickyNavbarJumpers } from "components/ui/ScrollJump";
import classNames from "components/ui/classNames";
import { Provider } from "jotai";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import { IAssetType, IIndicatorDataSource, ISecurityCountry } from "../../types/graphqlTypes";
import AssetDiscussion from "../discussions/AssetDiscussion";
import PropertiesToRent from "./PropertiesToRent";

interface IBrazilianRealEstateFundProps {
  issuer: IBrazilianRealEstateFundTopLevelFragment;
  tickerSymbol: string;
}

export default function BrazilianRealEstateFund({ issuer, tickerSymbol }: IBrazilianRealEstateFundProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "FIIs",
      issuerId: issuer._id,
      tickerSymbol: tickerSymbol.toUpperCase(),
      name: issuer.name,
      sector: issuer.selfRegulation.primarySegment,
    });
  }, [issuer._id]);

  if (!user) {
    return null;
  }

  return (
    <div>
      <FundamenteiMetadata
        title={`${issuer.name} - ${tickerSymbol} | Fundamentei`}
        description={`Análise fundamentalista completa e atualizada com os principais indicadores, dados e balanços, dentre outros do ${issuer.name} (${tickerSymbol})`}
      />

      <SpanPixelBeacon offset={-200} id={SectionJumpIdentifiers.Cover} />
      <div className="mx-auto max-w-7xl space-y-8 px-4 pb-8 xl:px-0">
        <div
          className={classNames(
            "relative mt-12 space-y-8 rounded-xl bg-white px-4 pb-8 pt-8 shadow",
            "border border-slate-200 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
          )}
        >
          <div className="px-4">
            <CompanyIssuerHeaderCard issuer={issuer} security={null} tickerSymbol={tickerSymbol} />
          </div>

          <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
            <div className="space-y-7">
              {user && (
                <div className="w-full px-4">
                  <h6 className="font-aeonik text-sm font-bold uppercase tracking-wide text-gray-600">
                    Avaliação dos Investidores
                  </h6>

                  <div className="mt-2">
                    <Rating
                      issuer={issuer}
                      rankingPlacement={issuer.overallRankingPlacement}
                      rankingRatingSummary={issuer.overallRankingRatingSummary}
                    />
                  </div>
                </div>
              )}

              <CompanyIssuerGovernance issuer={issuer} />
            </div>

            <div className="space-y-8">
              <ImpostoDeRenda issuer={issuer} />

              {issuer.distributionOfAssets && <DistributionOfAssets realEstateFund={issuer} />}
            </div>
          </div>
        </div>

        <StickyNavbarJumpers>
          <ScrollJumpButton id={SectionJumpIdentifiers.Cover}>
            <FormattedMessage defaultMessage="Início" id="m42V4+" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Liquidity}>
            <FormattedMessage defaultMessage="Cotação" id="zV6SED" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Financials}>
            <FormattedMessage defaultMessage="Balanços" id="wauBuH" />
          </ScrollJumpButton>

          <ScrollJumpButton id={SectionJumpIdentifiers.Discussions}>
            <FormattedMessage defaultMessage="Discussões" id="aDdimF" />
          </ScrollJumpButton>
        </StickyNavbarJumpers>

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <MarketCapCard issuer={issuer} />
          <FoundedYearCard issuer={issuer} />
          <IndexParticipationCard issuer={issuer} />
        </div>

        {issuer.trailingReturns && <TrailingReturnsChart trailingReturns={[issuer.trailingReturns]} />}

        <TwoPassInViewRender tag="QuotesChart">
          <Provider>
            <QuotesChart tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="TradesChart">
          <TradesChart tickerSymbols={[tickerSymbol]} />
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="SharesChart">
          <Provider>
            <SharesChart tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        <TwoPassInViewRender tag="MarketCapitalizationChart">
          <Provider>
            <MarketCapitalizationChart tickerSymbols={[tickerSymbol]} />
          </Provider>
        </TwoPassInViewRender>

        {hasPropertiesToRent(issuer.properties) && (
          <div className="grid w-full items-center">
            <PropertiesToRent realEstateFund={issuer} />
          </div>
        )}
        <FinancialsSection tickerSymbol={tickerSymbol} />

        <div className="mx-auto max-w-7xl space-y-8 px-4 pb-16 xl:px-0">
          {issuer.youtubeVideos.length > 0 && (
            <TwoPassInViewRender tag="YouTubeVideos">
              <div className="mt-6">
                <YouTubeVideos
                  key={issuer._id}
                  youtubeVideos={user && user.isPremium ? issuer.youtubeVideos : issuer.youtubeVideos.slice(0, 1)}
                />
              </div>
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="AssetDiscussion">
            <AssetDiscussion assetType={IAssetType.BrazilianRealEstateFund} assetId={issuer._id} />
          </TwoPassInViewRender>
        </div>
      </div>
    </div>
  );
}

function hasPropertiesToRent(properties: IBrazilianRealEstateFundTopLevelFragment["properties"]): boolean {
  return properties.some((property) => {
    return property.__typename === "BrazilianRealEstateFundPropertyToRent";
  });
}

interface FinancialsSectionProps {
  tickerSymbol: string;
}
function FinancialsSection({ tickerSymbol }: FinancialsSectionProps) {
  const { value: currentTableSettings } = useChangeSetting("financialsTable", {
    sortDirection: "ASC",
    textAlignment: "CENTER",
    fullBleed: false,
  });

  return (
    <div
      className={classNames("mx-auto max-w-7xl space-y-6 pb-16 lg:px-8 xl:px-0", {
        "max-w-full px-8 xl:px-8": currentTableSettings?.fullBleed,
      })}
    >
      <TwoPassInViewRender tag="Financials" loadingSkeletonClassName="mx-auto max-w-7xl space-y-8 pb-16 xl:px-0">
        <LocalFinancialsState indicatorDataSource={IIndicatorDataSource.Fnet} currency="BRL">
          {(props) => (
            <Provider>
              <Financials
                {...props}
                country={ISecurityCountry.Br}
                tickerSymbol={tickerSymbol}
                canChangeDataSource={false}
              />
            </Provider>
          )}
        </LocalFinancialsState>
      </TwoPassInViewRender>
    </div>
  );
}
